import * as React from 'react';
import { Box, CircularProgress, type SxProps, type Theme, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import * as echarts from 'echarts/core';
import type { ECharts, EChartsCoreOption, SetOptionOpts } from 'echarts/core';
import { BarChart, CustomChart, HeatmapChart, LineChart, PieChart, ScatterChart } from 'echarts/charts';
import {
  DataZoomComponent,
  DatasetComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  MarkPointComponent,
  TitleComponent,
  TooltipComponent,
  VisualMapComponent,
} from 'echarts/components';
import { CanvasRenderer, SVGRenderer } from 'echarts/renderers';
import { chartPalette } from './design';
import { palette } from '../theme/tokens';

echarts.use([
  BarChart,
  CustomChart,
  HeatmapChart,
  LineChart,
  PieChart,
  ScatterChart,
  DataZoomComponent,
  DatasetComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  MarkPointComponent,
  TitleComponent,
  TooltipComponent,
  VisualMapComponent,
  CanvasRenderer,
  SVGRenderer,
]);

export type AppChartOption = EChartsCoreOption;

type Props = {
  option: AppChartOption;
  height?: number | string;
  renderer?: 'canvas' | 'svg';
  loading?: boolean;
  sx?: SxProps<Theme>;
  setOptionOpts?: SetOptionOpts;
  events?: Record<string, (params: unknown, chart: ECharts) => void>;
  onReady?: (chart: ECharts) => void;
};

function mergeBaseOption(theme: Theme, option: AppChartOption): AppChartOption {
  const textColor = theme.palette.text.primary;
  const mutedColor = theme.palette.text.secondary;
  const divider = theme.palette.divider;
  const optionTooltip =
    option.tooltip && typeof option.tooltip === 'object' && !Array.isArray(option.tooltip)
      ? (option.tooltip as Record<string, unknown>)
      : {};
  const optionTooltipExtraCss =
    typeof optionTooltip.extraCssText === 'string' ? optionTooltip.extraCssText : '';

  return {
    ...option,
    color: [...chartPalette],
    animationDuration: 220,
    animationDurationUpdate: 180,
    textStyle: {
      fontFamily: theme.typography.fontFamily,
      color: textColor,
      fontSize: 12,
      ...(option.textStyle || {}),
    },
    grid: {
      left: 42,
      right: 18,
      top: 26,
      bottom: 34,
      containLabel: true,
      ...(option.grid || {}),
    },
    tooltip: {
      trigger: 'axis',
      confine: true,
      appendToBody: true,
      backgroundColor: theme.palette.mode === 'dark' ? palette.dark.surfaceRaised : theme.palette.background.paper,
      borderColor: theme.palette.mode === 'dark' ? palette.dark.borderHover : divider,
      borderWidth: 1,
      textStyle: {
        color: textColor,
        fontFamily: theme.typography.fontFamily,
        fontSize: 12,
      },
      ...optionTooltip,
      extraCssText: [
        `box-shadow: 0 12px 28px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.28 : 0.12)}`,
        'border-radius: 6px',
        'z-index: 900 !important',
        optionTooltipExtraCss,
      ].join(';'),
    },
    legend: {
      top: 0,
      right: 0,
      icon: 'roundRect',
      itemWidth: 12,
      itemHeight: 4,
      textStyle: {
        color: mutedColor,
        fontFamily: theme.typography.fontFamily,
        fontSize: 11,
      },
      ...(option.legend || {}),
    },
    xAxis: Array.isArray(option.xAxis)
      ? option.xAxis.map((axis) => axisDefaults(theme, axis))
      : option.xAxis
        ? axisDefaults(theme, option.xAxis)
        : option.xAxis,
    yAxis: Array.isArray(option.yAxis)
      ? option.yAxis.map((axis) => axisDefaults(theme, axis))
      : option.yAxis
        ? axisDefaults(theme, option.yAxis)
        : option.yAxis,
  };
}

function axisDefaults(theme: Theme, axis: unknown) {
  const axisObject = axis && typeof axis === 'object' ? axis as Record<string, unknown> : {};
  return {
    axisLine: { lineStyle: { color: theme.palette.divider } },
    axisTick: { show: false, lineStyle: { color: theme.palette.divider } },
    axisLabel: {
      color: theme.palette.text.secondary,
      fontFamily: theme.typography.fontFamily,
      fontSize: 11,
    },
    splitLine: {
      lineStyle: {
        color: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.105 : 0.07),
        opacity: theme.palette.mode === 'dark' ? 1 : undefined,
        type: 'dashed',
      },
    },
    ...axisObject,
  };
}

export default function AppChart({
  option,
  height = 280,
  renderer = 'canvas',
  loading = false,
  sx,
  setOptionOpts,
  events,
  onReady,
}: Props) {
  const theme = useTheme();
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const chartRef = React.useRef<ECharts | null>(null);
  const readyRef = React.useRef(onReady);

  readyRef.current = onReady;

  React.useEffect(() => {
    if (!containerRef.current) return undefined;
    const chart = echarts.init(containerRef.current, undefined, { renderer });
    chartRef.current = chart;
    readyRef.current?.(chart);

    const observer = new ResizeObserver(() => {
      chart.resize();
    });
    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
      chart.dispose();
      chartRef.current = null;
    };
  }, [renderer, theme.palette.mode]);

  React.useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    chart.setOption(mergeBaseOption(theme, option), {
      notMerge: true,
      lazyUpdate: false,
      ...setOptionOpts,
    });
    const frame = window.requestAnimationFrame(() => {
      if (!chart.isDisposed()) chart.resize();
    });
    const timer = window.setTimeout(() => {
      if (!chart.isDisposed()) chart.resize();
    }, 80);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [option, setOptionOpts, theme]);

  React.useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    if (loading) chart.showLoading('default', { color: theme.palette.primary.main });
    else chart.hideLoading();
  }, [loading, theme.palette.primary.main]);

  React.useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !events) return undefined;

    const handlers = Object.entries(events).map(([eventName, handler]) => {
      const wrapped = (params: unknown) => handler(params, chart);
      chart.on(eventName, wrapped);
      return [eventName, wrapped] as const;
    });

    return () => {
      handlers.forEach(([eventName, handler]) => chart.off(eventName, handler));
    };
  }, [events]);

  return (
    <Box
      sx={[
        {
          position: 'relative',
          width: '100%',
          height,
          minHeight: typeof height === 'number' ? height : undefined,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Box ref={containerRef} sx={{ width: '100%', height: '100%' }} />
      {loading && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', pointerEvents: 'none' }}>
          <CircularProgress size={22} />
        </Box>
      )}
    </Box>
  );
}
